import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import ru from "./ru";
import en from "./en";

/**
 * @typedef {"ru" | "en"} Locale
 */

const LOCALE_STORAGE_KEY = "cyberpass_locale";

/**
 * Dictionary mapping locale codes to translation objects.
 * @type {Record<Locale, Record<string, string>>}
 */
const dictionaries = { ru, en };

/**
 * @type {React.Context<{ locale: Locale; setLocale: (l: Locale) => void; t: (key: string, params?: Record<string, string | number>) => string }>}
 */
const I18nContext = createContext(null);

/**
 * Load saved locale from localStorage or default to "ru".
 * @returns {Locale}
 */
function getSavedLocale() {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved === "ru" || saved === "en") return saved;
  } catch {
    // localStorage may not be available
  }
  return "ru";
}

/**
 * Save locale to localStorage.
 * @param {Locale} locale
 */
function saveLocale(locale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}

/**
 * I18n provider component.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(getSavedLocale);

  const setLocale = useCallback((/** @type {Locale} */ newLocale) => {
    setLocaleState(newLocale);
    saveLocale(newLocale);
  }, []);

  const dict = dictionaries[locale] || ru;

  const t = useCallback(
    (
      /** @type {string} */ key,
      /** @type {Record<string, string | number> | undefined} */ params,
    ) => {
      let message = dict[key];
      if (message == null) {
        // Fallback: try Russian
        message = ru[key];
        if (message == null) {
          console.warn(`Missing i18n key: "${key}" for locale "${locale}"`);
          return key;
        }
      }
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          message = message.replace(`{${k}}`, String(v));
        }
      }
      return message;
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to use translations.
 * @returns {{ locale: Locale; setLocale: (l: Locale) => void; t: (key: string, params?: Record<string, string | number>) => string }}
 */
export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }
  return ctx;
}

export default I18nContext;
